/**
 * BackupCodes — eight single-use codes, shown once (A.4, FR-011, FR-013;
 * sheet 07b callout 5, sheet 40 Security card).
 *
 * This component owns no code state. The parent holds the codes in component
 * state only — never in the query cache, the URL or storage — passes them in,
 * and clears them when the user continues; with an empty list this renders
 * nothing, so a remount or a back navigation cannot bring the codes back.
 *
 * Codes render in `--font-mono` (CLAUDE.md §5). "Copy all" writes one code
 * per line. Continue is gated by "I have saved these codes" and, while
 * disabled, states that reason (style guide §7.1, §11).
 */
import { useId, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'

export interface BackupCodesProps {
  codes: string[]
  /** Called once the user has confirmed the codes are saved. Clear the codes here. */
  onContinue: () => void
  continueLabel?: string
}

export function BackupCodes({ codes, onContinue, continueLabel = 'Continue' }: BackupCodesProps) {
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const savedId = useId()
  const reasonId = useId()

  if (codes.length === 0) return null

  async function copyAll() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      setCopied(true)
    } catch {
      // No clipboard permission: the codes stay on screen and are selectable.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-body text-navy-700">
        Each code works once. Keep them somewhere safe and separate from your authenticator; they
        are your way in if you lose it.
      </p>
      <ol
        aria-label="Backup codes"
        className="grid grid-cols-2 gap-x-6 gap-y-2 rounded-lg border border-navy-200 bg-navy-50 p-4"
      >
        {codes.map((code) => (
          <li key={code} className="font-mono text-body text-navy-950">
            {code}
          </li>
        ))}
      </ol>
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" onClick={() => void copyAll()}>
          Copy all
        </Button>
        {copied && (
          <span role="status" className="text-caption text-navy-600">
            Copied
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        <Checkbox id={savedId} checked={saved} onCheckedChange={(v) => setSaved(v === true)} />
        <Label htmlFor={savedId} className="text-body font-normal">
          I have saved these codes
        </Label>
      </div>
      <div className="flex flex-col gap-2">
        <Button size="lg" disabled={!saved} aria-describedby={saved ? undefined : reasonId} onClick={onContinue}>
          {continueLabel}
        </Button>
        {!saved && (
          <p id={reasonId} className="text-caption text-navy-600">
            {continueLabel} is unavailable until you confirm you have saved these codes.
          </p>
        )}
      </div>
    </div>
  )
}
